import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import api from "../../../../Services/api";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast, ToastContainer } from "react-toastify";
import { useKeycloak } from "@react-keycloak/web";

import {
  keycloakbaseurl,
  createRole,
  updateRole,
  createRoleintoRealm,
  deletePermissionsFromRole,
  getAllPermissions,
  getRoleDataByRoleNameURL,
  addPermissionsToRole,
  getAllPermissionsMobile,
} from "../../../../Services/EndPoints";
import {
  CreateRoleConfig,
  schema,
} from "../../../../JsonData/CreateRoleConfig";
import {
  postDataWithBody,
  getData,
  putDataWithBody,
} from "../../../../Services/Services";
import { useTranslation } from "react-i18next";
import navLinks from "../../../../JsonData/navLinksData";

import { yupResolver } from "@hookform/resolvers/yup";

const collectPermissionIds = (links, ids = []) => {
  links.forEach(link => {
    if (link.allowedPermissionIds) {
      ids.push(...link.allowedPermissionIds);
    }
    if (link.children && link.children.length > 0) {
      collectPermissionIds(link.children, ids);
    }
  });
  return ids;
};


// ---------------------------------------------------------------------------
// Helpers (defined outside component for stable references)
// ---------------------------------------------------------------------------
const MENU_ORDER_IDS = collectPermissionIds(navLinks);

function sortByMenuOrder(nodes) {
  return nodes
    .sort((a, b) => {
      const idxA = MENU_ORDER_IDS.indexOf(a.id);
      const idxB = MENU_ORDER_IDS.indexOf(b.id);
      
      // If both are found in the menu, sort by menu order
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      
      // If one is found and the other isn't, the found one comes first
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      
      // If neither is found, maintain existing order (return 0) or sort by ID
      return (a.id || 0) - (b.id || 0);
    })
    .map((node) => ({
      ...node,
      children: node.children && node.children.length > 0 
        ? sortByMenuOrder(node.children) 
        : [],
    }));
}
function flatToHier(flat) {
  const roots = [];
  const all = {};

  flat.forEach((item) => {
    all[item.id] = { ...item, label: item.name, value: item.id };
  });

  Object.values(all).forEach((item) => {
    // Treat self-referencing nodes as root-level
    if (item.parent_id == null || item.parent_id === item.id) {
      roots.push(item);
    } else if (all[item.parent_id]) {
      const parent = all[item.parent_id];
      if (!parent.children) parent.children = [];
      parent.children.push(item);
    }
  });

  return sortByMenuOrder(roots);
}

function addSelectedFlag(data, selectedValues) {
  return data.map(({ children = [], ...rest }) => {
    const node = { ...rest };
    if (selectedValues.includes(node.id)) node.checked = true;
    if (children.length) node.children = addSelectedFlag(children, selectedValues);
    return node;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RoleRegistrationForm = () => {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  const dataFrEdit = state?.data;

  // ── form state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [roleConfig, setRoleConfig] = useState(CreateRoleConfig);
  const [initDataLoadComplete, setInitDataLoadComplete] = useState(false);
  const [mobilePermissionsReady, setMobilePermissionsReady] = useState(false);

  // permission tracking
  const [rolePermissionData, setRolePermissionData] = useState([]);
  const [permissionDataFrmServiceMobile, setPermissionDataFrmServiceMobile] =
    useState([]);

  // field disabled map
  const [isFieldDisabled, setIsFieldDisabled] = useState({
    web_permission: false,
    Mobile_permission: false,
  });

  // Key incremented to force full remount of the tree component when web is cleared
  const [webTreeKey, setWebTreeKey] = useState(0);

  // Ref: hidden 3rd-level children map { parentId: [childId, ...] }
  const hiddenChildrenMap = useRef({});
  // Ref: Full parent -> children mapping for recursive selection
  const childAdjacencyList = useRef({});
  // Ref: pristine (no checked flags) web permission tree
  const pristineWebTree = useRef([]);

  // ── react-hook-form ───────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema[activeTab]), mode: "all" });

  // Register hidden "permission" field once
  useEffect(() => {
    register("permission");
  }, [register]);

  const userAccessType = watch("User Acess Type", { web: false, mobile: false });
  const prevAccessType = useRef({ web: undefined, mobile: undefined });

  // Handle userAccessType changes with validation
  useEffect(() => {
    const accessType = userAccessType || {};
    const hasWeb = Boolean(accessType.web);
    const hasMobile = Boolean(accessType.mobile);
    const prev = prevAccessType.current;

    // Check if we're trying to turn OFF web access with permissions
    if (prev.web === true && !hasWeb) {
      const currentPermissions = getValues("permission");
      if (currentPermissions && currentPermissions.trim() !== "") {
        trigger("permission");
      }
    }

    // Check if we're trying to turn OFF mobile access with permissions
    if (prev.mobile === true && !hasMobile) {
      const currentMobilePerms = getValues("Mobile_permission");
      if (currentMobilePerms && currentMobilePerms.length > 0) {
        trigger("Mobile_permission");
      }
    }

    prevAccessType.current = { web: hasWeb, mobile: hasMobile };

    setIsFieldDisabled({
      web_permission: !hasWeb,
      Mobile_permission: !hasMobile,
    });

    // Clear fields when access type is disabled
    if (!hasWeb) {
      setValue("permission", "");
      // Increment key to force tree component remount
      setWebTreeKey(prev => prev + 1);
    }

    if (!hasMobile) {
      setValue("Mobile_permission", []);
    }
  }, [userAccessType, setValue, getValues, trigger]);

  // ── init data load ────────────────────────────────────────────────────────
  useEffect(() => {
    getAllUserPermission();
    getMobileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getAllUserPermission = async () => {
    const permData = await getData(getAllPermissions);

    const webPermissionData = permData.filter(
      (p) => p.menuType !== "M"
    );

    hiddenChildrenMap.current = {};
    childAdjacencyList.current = {};
    const hiddenIds = new Set();

    webPermissionData.forEach((item) => {
      // Populate adjacency list for all relationships
      if (item.parent_id !== null && item.parent_id !== item.id) {
        if (!childAdjacencyList.current[item.parent_id]) {
          childAdjacencyList.current[item.parent_id] = [];
        }
        childAdjacencyList.current[item.parent_id].push(item.id);
      }
      // ✅ Skip self-referencing items — treat them as root-level nodes
      if (item.parent_id === item.id) return;

      if (item.parent_id !== null) {
        const parent = webPermissionData.find((p) => p.id === item.parent_id);
        if (parent && parent.parent_id !== null && parent.parent_id !== parent.id) {
          hiddenIds.add(item.id);
          if (!hiddenChildrenMap.current[item.parent_id]) {
            hiddenChildrenMap.current[item.parent_id] = [];
          }
          hiddenChildrenMap.current[item.parent_id].push(item.id);
        }
      }
    });

    const filtered = webPermissionData.filter((item) => !hiddenIds.has(item.id));

    // Sort to match sidebar menu order from navLinksData
    const menuOrder = collectPermissionIds(navLinks);
    filtered.sort((a, b) => {
      const idxA = menuOrder.indexOf(a.id);
      const idxB = menuOrder.indexOf(b.id);
      // Items found in menu order come first, in menu order;
      // items not found go to the end, preserving their original order
      if (idxA === -1 && idxB === -1) return 0;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });

    const hierarchy = flatToHier(filtered);


    // Store a pristine deep copy
    pristineWebTree.current = JSON.parse(JSON.stringify(hierarchy));

    // Update config immutably
    setRoleConfig((prev) =>
      prev.map((tab, tabIdx) => ({
        ...tab,
        fields: tab.fields.map((field, fieldIdx) =>
          tabIdx === 0 && fieldIdx === 2 ? { ...field, options: hierarchy } : field
        ),
      }))
    );
    setInitDataLoadComplete(true);
  };

  const getMobileData = () => {
    getData(getAllPermissionsMobile)
      .then((permData) => {
        const mobile = permData.filter((p) => p.menuType === "M");
        setPermissionDataFrmServiceMobile(mobile);
        setMobilePermissionsReady(true);

        const formattedOptions = mobile.map((p) => ({
          label: p.name,
          value: p.id,
          id: p.id,
        }));

        setRoleConfig((prev) =>
          prev.map((tab) => ({
            ...tab,
            fields: tab.fields.map((field) =>
              field.name === "Mobile_permission"
                ? { ...field, options: formattedOptions }
                : field
            ),
          }))
        );
      })
      .catch((err) => console.error("Error fetching mobile permissions:", err));
  };

  // ── populate edit data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!initDataLoadComplete || !mobilePermissionsReady || !dataFrEdit) return;

    // Set basic form values
    setValue("name", dataFrEdit.name);
    setValue("description", dataFrEdit.description);

    // Set user access types
    const userAccessTypes = dataFrEdit.userAccessType
      ? dataFrEdit.userAccessType.split(",")
      : [];
    setValue("User Acess Type", {
      web: userAccessTypes.includes("W"),
      mobile: userAccessTypes.includes("M"),
    });

    // Web tree permissions
    if (dataFrEdit.role_permission?.length) {
      const webPerms = dataFrEdit.role_permission.filter(
        (p) => p.userAccessType === "W" || !p.userAccessType
      );

      if (webPerms.length) {
        const webIds = webPerms.map((p) => p.permission_id);

        setRolePermissionData(webPerms.map((p) => ({ permission_id: p.permission_id })));
        setValue("permission", webIds.join(","));

        setRoleConfig((prev) =>
          prev.map((tab, tabIdx) => ({
            ...tab,
            fields: tab.fields.map((field, fieldIdx) => {
              if (tabIdx === 0 && fieldIdx === 2) {
                return {
                  ...field,
                  options: addSelectedFlag(field.options || [], webIds),
                };
              }
              return field;
            }),
          }))
        );
      }
    }

    // Mobile dropdown permissions
    setTimeout(() => {
      if (dataFrEdit.role_permission?.length && permissionDataFrmServiceMobile.length) {
        const mobilePermIds = dataFrEdit.role_permission
          .filter((p) => p.userAccessType === "M")
          .map((p) => p.permission_id);

        if (mobilePermIds.length) {
          const selected = permissionDataFrmServiceMobile
            .filter((p) => mobilePermIds.includes(p.id))
            .map((p) => ({ label: p.name, value: p.id, id: p.id }));
          setValue("Mobile_permission", selected);
        }
      }
    }, 300);
  }, [
    initDataLoadComplete,
    mobilePermissionsReady,
    dataFrEdit,
    permissionDataFrmServiceMobile,
    setValue,
  ]);

  // ── permission tree change handler ────────────────────────────────────────
  const onHierDropdownChange = useCallback(
    (currentNode, selectedNodes) => {
      const permissionArr = [];
      const visited = new Set();

      const addNodeAndDescendants = (nodeId) => {
        if (!nodeId || visited.has(nodeId)) return;
        visited.add(nodeId);
        permissionArr.push({ permission_id: nodeId });

        // Recursively add all descendants (both visible and hidden)
        const children = childAdjacencyList.current[nodeId] || [];
        children.forEach((childId) => addNodeAndDescendants(childId));
      };

      selectedNodes.forEach((node) => {
        addNodeAndDescendants(node.id);
      });

      // deduplicate
      const unique = Array.from(
        new Map(permissionArr.map((p) => [p.permission_id, p])).values()
      );

      setRolePermissionData(unique);
      setValue("permission", unique.map((p) => p.permission_id).join(","));
    },
    [setValue]
  );

  // ── keycloak helpers ──────────────────────────────────────────────────────
  const getLoggedInUserAuthToken = useCallback(
    () => keycloak.token,
    [keycloak]
  );

  const getURMHeader = useCallback(async () => {
    const token = await getLoggedInUserAuthToken();
    return {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [getLoggedInUserAuthToken]);

  const getRoleDataByRoleName = useCallback(
    async (roleName) => {
      const url =
        keycloakbaseurl +
        getRoleDataByRoleNameURL.replace(
          "{REALM}",
          process.env.REACT_APP_URM_REALM
        ) +
        roleName;
      const headers = await getURMHeader();
      const response = await api.getData(url, null, headers);
      return response.data;
    },
    [getURMHeader]
  );

  // ── app role CRUD ─────────────────────────────────────────────────────────
  const createRoleInApp = async (input) => {
    const payload = { ...input, status: true };
    delete payload.attributes;
    return postDataWithBody(createRole, payload, {});
  };

  const updateRoleInApp = async (input) => {
    const payload = { ...input, status: true };
    delete payload.attributes;
    return putDataWithBody(updateRole, payload, {});
  };

  // ── toast helpers ─────────────────────────────────────────────────────────
  const showSuccessMsg = (msg) =>
    toast.success(msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });

  const showErrorMsg = (msg) =>
    toast.error(msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });

  // ── assign permissions ────────────────────────────────────────────────────
  const assignPermissions = async (roleId, webPermissionsData, isUpdate) => {
    const mobilePerms = getValues("Mobile_permission") || [];
    const formattedPermissions = [];

    webPermissionsData.forEach((perm) => {
      formattedPermissions.push({ permission_id: perm.permission_id, userAccessType: "W" });
    });

    mobilePerms.forEach((perm) => {
      formattedPermissions.push({
        permission_id: perm.id || perm.value,
        userAccessType: "M",
      });
    });

    const roleObj = { id: roleId, role_permission: formattedPermissions };
    const res = await postDataWithBody(addPermissionsToRole + roleId, roleObj, {});

    if (res.status === "success") {
      showSuccessMsg(`Role ${isUpdate ? "updated" : "created"} successfully.`);
      navigate("../roles");
    } else {
      showErrorMsg(`An error occurred while ${isUpdate ? "updating" : "creating"} the role.`);
    }
  };

  const submitHandler = async (data, e) => {
    e.preventDefault();

    const accessTypeString = Object.keys(data["User Acess Type"] || {})
      .filter((key) => data["User Acess Type"][key])
      .map((key) => (key === "web" ? "W" : key === "mobile" ? "M" : ""))
      .join(",");

    // Note: Admin_Access is commented out in the config, so not included
    const inputobj = {
      name: data.name,
      description: data.description,
      attributes: { created_on: [new Date().getTime()] },
      userAccessType: accessTypeString,
    };

    // Check if web access is enabled before including web permissions
    const hasWebAccess = data["User Acess Type"]?.web || false;

    let currentWebPermissions = [];

    if (hasWebAccess) {
      const permissionString = getValues("permission");

      if (permissionString) {
        currentWebPermissions = permissionString
          .split(",")
          .filter(Boolean)
          .map((id) => ({ permission_id: id }));
        setRolePermissionData(currentWebPermissions);
      } else if (dataFrEdit?.role_permission && hasWebAccess) {
        currentWebPermissions = dataFrEdit.role_permission
          .filter((p) => p.userAccessType === "W" || !p.userAccessType)
          .map((p) => ({ permission_id: p.permission_id }));
      }
    } else {
      // Explicitly clear web permissions when web access is disabled
      setRolePermissionData([]);
      setValue("permission", "");
    }

    if (dataFrEdit) {
      // UPDATE flow
      inputobj.id = dataFrEdit.id;

      const delRes = await putDataWithBody(
        deletePermissionsFromRole + dataFrEdit.id,
        {},
        {}
      );

      if (delRes.status === "success") {
        const updRes = await updateRoleInApp(inputobj);
        if (updRes.id) {
          await assignPermissions(dataFrEdit.id, currentWebPermissions, true);
        }
      }
    } else {
      // CREATE flow
      const url =
        keycloakbaseurl +
        createRoleintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
      const headers = await getURMHeader();

      try {
        const response = await api.postData(
          url,
          headers,
          JSON.stringify({
            name: inputobj.name,
            description: inputobj.description,
            attributes: inputobj.attributes,
          })
        );

        if (response.status === 201) {
          const roleobj = await getRoleDataByRoleName(data.name);
          inputobj.urm_role_id = roleobj.id;
          const createRes = await createRoleInApp(inputobj);
          if (createRes.id) {
            await assignPermissions(createRes.id, currentWebPermissions, false);
          }
        }
      } catch (err) {
        showErrorMsg(err.response?.data?.errorMessage || "An error occurred");
      }
    }
  };

  // ── form render helpers ───────────────────────────────────────────────────
  const GenerateForm = useCallback(
    (fields) => (
      <div>
        {initDataLoadComplete && (
          <FormHandler
            register={register}
            errors={errors}
            fields={fields}
            onChange={onHierDropdownChange}
            control={control}
            columns={2}
            getValues={getValues}
            webaccess={userAccessType?.mobile}
            mobileAccess={userAccessType?.web}
            isFieldDisabled={isFieldDisabled}
            webTreeKey={webTreeKey}
          />
        )}
      </div>
    ),
    [
      initDataLoadComplete,
      register,
      errors,
      onHierDropdownChange,
      control,
      getValues,
      userAccessType,
      isFieldDisabled,
      webTreeKey,
    ]
  );

  const tabsData = roleConfig.map((tab) => ({
    ...tab,
    heading: dataFrEdit && tab.heading === "Create Role" ? "Edit Role" : tab.heading,
    component: GenerateForm(
      dataFrEdit 
        ? tab.fields.map(f => f.name === "name" ? { ...f, disabled: true } : f)
        : tab.fields
    ),
  }));

  // ── tab navigation ────────────────────────────────────────────────────────
  const saveTabDataHandler = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveTab((prev) => prev + 1);
  };

  const previousHandler = () => {
    if (activeTab > 0) setActiveTab((prev) => prev - 1);
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="mt-4">
      <ToastContainer />
      <div className="form-body">
        <div className="registration__form--body">
          <form onSubmit={handleSubmit(submitHandler)} name="RoleRegForm">
            <div>
              {tabsData.map((tab) =>
                tab.id === activeTab ? (
                  <React.Fragment key={tab.id}>
                    <div className="registration__form--heading">{tab.heading}</div>
                    <div>{tab.component}</div>
                  </React.Fragment>
                ) : null
              )}
            </div>

            <div className="d-flex justify-content-center">
              {activeTab > 0 && (
                <Button
                  variant="secondary"
                  className="previousbutton-style mx-2"
                  onClick={previousHandler}
                >
                  Previous
                </Button>
              )}
              {tabsData.length - 1 === activeTab ? (
                <Button type="submit" className="mx-2" variant="primary">
                  {t("saveAndSubmit")}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  className="mx-2"
                  onClick={saveTabDataHandler}
                >
                  Save & Next
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleRegistrationForm;